import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "react-toastify";

// prime react
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
// components
import Panel from "../../../components/panel/panel";
import ExportTable from "../../../components/button/ExportButton";
import { useAppContext } from "../../../context/AppContext";
import { useFinancialContext } from "../../../context/FinancialContext";
//services
import { getAccountToPayFiles } from "../../../services/Financial/AccountToPay/getAccountToPayFiles";
// utils
import { formatDate } from "../../../utils/formatDate";
import { formatCurrency } from "../../../utils/formatCurrency";

export const TableAccount = ({
  dataTable,
  totalRecords,
  globalFilterValue,
  onGlobalFilterChange,
  isLoading,
  rows,
  first,
  onPageChange,
  rowsPerPageOptions = [5, 10, 20, 50, 100],
}) => {
// console.log("onPageChange:", onPageChange);
// console.log("dataTable:", dataTable);
  const { nomenclatures } = useAppContext();
  const { selectedCustomers, setSelectedCustomers, isVisibleButtons } = useFinancialContext();
  const [selectAll, setSelectAll] = useState(false);
  const [tableToExport, setTableToExport] = useState(null);
  const nomenclature = nomenclatures ? JSON.parse(nomenclatures) : [];
  const [visible, setVisible] = useState(false);
  const [previewDoc, setPreviewDoc] = useState([]);
  const { permissions } = useAppContext();

  useEffect(() => {
    const timer = setTimeout(() => {
      // console.log("Resetando seleções - dados mudaram");
      setSelectedCustomers([]);
      setSelectAll(false);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [dataTable, setSelectedCustomers]);
  const viewDocument = (rowData) => {
      console.log("Documento selecionado:", rowData);
      viewFile(rowData.id, rowData.fileType, rowData.fileName);
      setVisible(true);
  };
  const viewFile = async (id, typeFile, name) => {
    setVisible(true);
    const notification = toast.loading("Por favor aguarde, carregando...");
  
    try {
      const response = await getAccountToPayFiles(id);
      if (response && response.data) {
        setPreviewDoc([
          {
            uri: response.data.fileUrl,
            fileType: typeFile,
            fileName: name,
          },
        ]);
        toast.update(notification, {
          render: "Documento Carregado!",
          autoClose: 1000,
          theme: "colored",
          type: "success",
          isLoading: false,
        });
      } else {
        throw new Error("No file data");
      }
    } catch (error) {
      toast.update(notification, {
        render: "Não foi possível visualizar o arquivo",
        autoClose: 1000,
        theme: "colored",
        type: "warning",
        isLoading: false,
      });
    }
  };

  const columns = [
    { field: "id", header: "Nº Lançamento" },
    { field: "sequence_number", header: "Nº Parcela" },
    {
      field: "client_name", // Campo processado para ordenação
      header: "Cliente",
      body: (rowData) => rowData.client_name,
    },    
    { 
      field: "description_name", // Campo processado para ordenação
      header: "Descrição",
      body: (rowData) => rowData.description_name,
    },

    {
      field: "status_text", // Campo processado para ordenação
      header: "Situação",
      body: (rowData) => {
        let labelClass = "";
        let labelText = "";
        switch (rowData.status) {
          case 0:
            labelClass = "badge bg-warning";
            labelText = "Em Aberto";
            break;
          case 1:
            labelClass = "badge bg-success";
            labelText = "Pago";
            break;
          case 2:
            labelClass = "badge bg-dark";
            labelText = "Ib Juridico";
            break;
          case 3:
            labelClass = "badge bg-danger";
            labelText = "Em Renegociação";
            break;
          default:
        }

        return (
          <span
            className={labelClass}
            style={{
              fontSize: "10px",
              marginLeft: "15px",
              marginRight: "-40px",
            }}
          >
            {labelText}
          </span>
        );
      },
    },
    {
      field: "block",
      header: `${nomenclature[0]?.value || "Quadra"}/${
        nomenclature[1]?.value || "Lote"
      }`,
    },
    {
      field: "due_date",
      header: "Data vencimento",
      body: (rowData) => formatDate(rowData.due_date),
    },
    {
      field: "value",
      header: "Valor parcela",
      body: (rowData) => formatCurrency(rowData.value),
    },
    { 
      field: "enterprise_title", // Campo processado para ordenação
      header: "Empreendimento",
      body: (rowData) => rowData.enterprise_title,
    },
    {
      field: "payment_date",
      header: "Data recebimento",
      body: (rowData) => formatDate(rowData.payment_date),
    },
    {
      field: "discount",
      header: "Desconto",
      body: (rowData) => formatCurrency(rowData.discount || 0),
    },
    {
      field: "fine",
      header: "Multa",
      body: (rowData) => formatCurrency(rowData.fine || 0),
    },
    {
      field: "daily_interest_rate",
      header: "Juros",
      body: (rowData) => formatCurrency(rowData.daily_interest_rate || 0),
    },
    {
      field: "addition",
      header: "Acréscimos",
      body: (rowData) => formatCurrency(rowData.addition || 0),
    },
    {
      field: "honorary",
      header: "Honorários",
      body: (rowData) => formatCurrency(rowData.honorary || 0),
    },
    {
      field: "payment_value",
      header: "Valor pago",
      body: (rowData) => formatCurrency(rowData.payment_value || 0),
    },
    { 
      field: "payment_method_name", // Campo processado para ordenação
      header: "Forma de pagamento",
      body: (rowData) => rowData.payment_method_name,
    },
    { 
      field: "cost_center_desc", // Campo processado para ordenação
      header: "Centro de Receita",
      body: (rowData) => rowData.cost_center_desc,
    },
    { 
      field: "released_by_name", // Campo processado para ordenação
      header: "Lançado por",
      body: (rowData) => rowData.released_by_name,
    },
    { 
      field: "completed_by_name", // Campo processado para ordenação
      header: "Baixado por",
      body: (rowData) => rowData.completed_by_name,
    },
     ...(permissions.includes("188")
          ? [
              {
                field: "actions",
                header: "Documentos",
                body: (rowData) => (
                  <Button
                    icon="pi pi-eye"
                    className="p-button-text"
                    onClick={() => viewDocument(rowData)}
                    tooltip="Visualizar Documento"
                  />
                ),
              },
            ]
          : []),
    { field: "shipping_code", header: "Código do lote de remessa" },
    { field: "cod_link", header: "Vinculo" },
  ];

  const exportColumns = columns.map((col) => ({
    title: col.header,
    dataKey: col.field,
  }));

  // Selection handled via custom checkbox templates; no DataTable selection state used

  const processedDataTable = useMemo(() => {
    if (!dataTable) return [];
    
    return dataTable.map(item => ({
      ...item,
      // Campos processados para ordenação
      client_name: item.payer?.name || item.sale?.client?.name || "Sem cliente",
      description_name: item.description?.name || "",
      enterprise_title: item.enterprise?.title || "",
      payment_method_name: item.payment_method?.name || "",
      cost_center_desc: item.cost_center?.desc || "",
      released_by_name: item.released_by?.name || "",
      completed_by_name: item.completed_by?.name || "",
      // Status como texto para ordenação
      status_text: (() => {
        switch (item.status) {
          case 0: return "Em Aberto";
          case 1: return "Pago";
          case 2: return "Ib Juridico";
          case 3: return "Em Renegociação";
          default: return "";
        }
      })()
    }));
  }, [dataTable]);

  // Função para verificar se uma linha pode ser selecionada (memoizada)
  const isRowSelectable = useCallback((data) => {
    if (!isVisibleButtons) {
      return data.shipping === 3;
    }
    return ![1, 2, 5, 6].includes(data.shipping);
  }, [isVisibleButtons]);

  const selectableItems = useMemo(() => {
    return processedDataTable ? processedDataTable.filter(isRowSelectable) : [];
  }, [processedDataTable, isRowSelectable]);

  // Track selection by ID to avoid reference issues after sorting/rerenders
  const selectedIdsSet = useMemo(() => {
    return new Set((selectedCustomers || []).map((s) => s?.id));
  }, [selectedCustomers]);

  const checkboxTemplate = useCallback((rowData) => {
    if (!isRowSelectable(rowData)) {
      return null; 
    }

    const isSelected = selectedIdsSet.has(rowData?.id);
    
    return (
      <Checkbox
        checked={isSelected}
        onChange={(e) => {
          if (e.checked) {
            setSelectedCustomers((prev = []) => {
              // prevent duplicates by id
              const exists = (prev || []).some((p) => p?.id === rowData?.id);
              return exists ? prev : [...prev, rowData];
            });
          } else {
            setSelectedCustomers((prev = []) => (prev || []).filter(item => item?.id !== rowData?.id));
          }
        }}
      />
    );
  }, [isRowSelectable, selectedIdsSet, setSelectedCustomers]);

  const headerCheckboxTemplate = useCallback(() => {
    const allSelectableSelected = selectableItems.length > 0 && 
      selectableItems.every(item => selectedIdsSet.has(item?.id));
    
    return (
      <Checkbox
        checked={allSelectableSelected}
        onChange={(e) => {
          if (e.checked) {
            setSelectedCustomers((prev = []) => {
              const byId = new Map((prev || []).map(p => [p?.id, p]));
              selectableItems.forEach(item => {
                if (item?.id != null && !byId.has(item.id)) {
                  byId.set(item.id, item);
                }
              });
              return Array.from(byId.values());
            });
          } else {
            const selectableIds = new Set(selectableItems.map(it => it?.id));
            setSelectedCustomers((prev = []) => (prev || []).filter(item => !selectableIds.has(item?.id)));
          }
        }}
      />
    );
  }, [selectableItems, selectedIdsSet, setSelectedCustomers]);

  useEffect(() => {
    if (selectedCustomers.length === 0) {
      setSelectAll(false);
    }
  }, [selectedCustomers]);

  useEffect(() => {
    if (processedDataTable) {
      const dataWithoutIds = processedDataTable.map((t) => {
        let labelText = "";
        switch (t.status) {
          case 0:
            labelText = "Em Aberto";
            break;
          case 1:
            labelText = "Pago";
            break;
          case 2:
            labelText = "Ib Juridico";
            break;
          case 3:
            labelText = "Em Renegociação";
            break;
          default:
        }
        return {
          numeroLancamento: t.ids,
          NumeroParcela: t.sequence_number,
          NomeCliente: t.client_name,
          Descricao: t.description_name,
          Situacao: labelText,
          QuadraLote: t.block,
          DataVencimento: formatDate(t.due_date),
          ValorParcela: formatCurrency(t.value),
          Empreendimento: t.enterprise_title,
          DataRecebimento: formatDate(t.payment_date),
          Desconto: formatCurrency(t.discount || 0),
          Multa: formatCurrency(t.fine || 0),
          Juros: formatCurrency(t.daily_interest_rate || 0),
          Honorarios: formatCurrency(t.honorary || 0),
          ValorPago: formatCurrency(t.payment_value || 0),
          FormaPagamento: t.payment_method_name,
          CentroReceita: t.cost_center_desc,
          LancadoPor: t.released_by_name,
          BaixadoPor: t.completed_by_name,
          CodigoLote: t.shipping_code,
          Vinculo: t.cod_link,
        };
      });
      setTableToExport(dataWithoutIds);
    }
  }, [processedDataTable]);

  return (
    <Panel mainCss="mt-4">
      <div className="px-2 pb-3">
        <ExportTable
          tableData={tableToExport}
          exportColumns={exportColumns}
          tabelaKey="Relatório de Contas a Receber"
          globalFilterValue={globalFilterValue}
          onGlobalFilterChange={onGlobalFilterChange}
          orientation="landscape"
        />
        <DataTable
          value={processedDataTable}
          loading={isLoading}
          stripedRows
          showGridlines
          sortMode="multiple"
          dataKey="id"
          globalFilter={globalFilterValue}
          emptyMessage="Nenhuma Informação foi encontrada."
          tableStyle={{ minWidth: "0.8rem", fontSize: "0.7rem" }}
        >
          <Column
            headerStyle={{ width: "3rem" }}
            body={checkboxTemplate}
            header={headerCheckboxTemplate}
          />
          {columns.map((col, i) => (
            <Column
              sortable
              body={col.body}
              key={i}
              style={{ minWidth: 150 }}
              field={col.field}
              header={col.header}
            />
          ))}
        </DataTable>
      </div>
    </Panel>
  );
};